import { configureStore as configureReduxStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import LogRocket from 'logrocket';
import * as R from 'ramda';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { APP_SETTINGS_SLICE_KEY, appSettingsReducer } from './app-settings';
import { bffApi } from './bff-api';
import {
  MEMBER_REPORTING_SLICE_KEY,
  memberReportingReducer,
} from './member-reporting';
import { ONBOARDING_SLICE_KEY, onboardingReducer } from './onboarding';
import { PLAYER_SLICE_KEY, playerReducer } from './player';
import { PLAYER_FSM_SLICE_KEY, playerFsmReducer } from './player-fsm';
import { postgrestApi } from './postgrest-api';
import { SESSION_SLICE_KEY, sessionReducer } from './session';
import { errorLogger } from './store.utils';
import { UI_SLICE_KEY, uiReducer } from './ui';
import {
  USER_PREFERENCES_SLICE_KEY,
  userPreferencesReducer,
} from './user-preferences';

const persistConfig = {
  [USER_PREFERENCES_SLICE_KEY]: {
    key: USER_PREFERENCES_SLICE_KEY,
    storage,
  },
};

export const rootReducer = {
  [postgrestApi.reducerPath]: postgrestApi.reducer,
  [bffApi.reducerPath]: bffApi.reducer,
  [PLAYER_SLICE_KEY]: playerReducer,
  [PLAYER_FSM_SLICE_KEY]: playerFsmReducer,
  [SESSION_SLICE_KEY]: sessionReducer,
  [ONBOARDING_SLICE_KEY]: onboardingReducer,
  [UI_SLICE_KEY]: uiReducer,
  [APP_SETTINGS_SLICE_KEY]: appSettingsReducer,
  [MEMBER_REPORTING_SLICE_KEY]: memberReportingReducer,
  [USER_PREFERENCES_SLICE_KEY]: persistReducer(
    persistConfig[USER_PREFERENCES_SLICE_KEY],
    userPreferencesReducer,
  ),
};

export const rootMiddleware = [postgrestApi.middleware, bffApi.middleware];

export const configureStore = () =>
  configureReduxStore({
    reducer: rootReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: {
          warnAfter: 100,
        },
      }).concat(
        ...rootMiddleware,
        errorLogger,
        LogRocket.reduxMiddleware({
          // LogRocket should be the last middleware in the list
          stateSanitizer: state => ({
            ...state,
            episodes: {},
            postgrestApi: {
              ...state.postgrestApi,
              queries: {
                ...state.postgrestApi.queries,
                // Remove the data field from all queries
                ...R.mapObjIndexed(
                  (query: object) => ({
                    ...query,
                    data: undefined,
                  }),
                  state.postgrestApi.queries,
                ),
              },
            },
            bffApi: {
              ...state.bffApi,
              queries: {
                ...state.bffApi.queries,
                // Remove the data field from all queries
                ...R.mapObjIndexed(
                  (query: object) => ({
                    ...query,
                    data: undefined,
                  }),
                  state.bffApi.queries,
                ),
              },
            },
          }),
        }),
      ),
    devTools: process.env.NODE_ENV !== 'production',
  });

export const store = configureStore();
export const persistor = persistStore(store);

setupListeners(store.dispatch);

export const useAppDispatch = () => useDispatch<typeof store.dispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export type RootState = ReturnType<typeof store.getState>;
